<script>
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
export default {
  props: {
    name: {
      type: String,
      default: '',
    },
    email: {
      type: String,
      default: '',
    },
    displayName: {
      type: String,
      default: '',
    },
    emailEnabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['updateUser'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      userName: this.name,
      userDisplayName: this.displayName,
      userEmail: this.email,
      editingField: null,
    };
  },
  validations: {
    userName: {
      required,
      minLength: minLength(1),
    },
    userDisplayName: {},
    userEmail: {
      required,
      email,
    },
  },
  watch: {
    name: {
      handler(value) {
        this.userName = value;
      },
      immediate: true,
    },
    displayName: {
      handler(value) {
        this.userDisplayName = value;
      },
      immediate: true,
    },
    email: {
      handler(value) {
        this.userEmail = value;
      },
      immediate: true,
    },
  },
  methods: {
    activateField(field) {
      this.editingField = field;
      const inputRef = {
        name: 'profileNameInput',
        displayName: 'profileDisplayNameInput',
        email: 'profileEmailInput',
      }[field];
      this.$nextTick(() => this.$refs[inputRef]?.focus());
    },
    saveField(field) {
      const validationField = {
        name: 'userName',
        displayName: 'userDisplayName',
        email: 'userEmail',
      }[field];
      const validation = this.v$[validationField];
      validation?.$touch();

      if (validation?.$invalid) {
        useAlert(this.$t('PROFILE_SETTINGS.FORM.ERROR'));
        return;
      }

      this.$emit('updateUser', {
        name: this.userName,
        displayName: this.userDisplayName,
        email: this.userEmail,
      });
      this.editingField = null;
    },
  },
};
</script>

<template>
  <section class="profile-basic-details flex flex-col gap-4">
    <div
      class="profile-basic-details__field"
      :class="{
        'profile-basic-details__field--editing': editingField === 'name',
        'profile-basic-details__field--error': v$.userName.$error,
      }"
    >
      <label class="profile-basic-details__label" for="profile-name">
        {{ $t('PROFILE_SETTINGS.FORM.NAME.LABEL') }}
      </label>
      <div class="profile-basic-details__input-row">
        <input
          id="profile-name"
          ref="profileNameInput"
          v-model="userName"
          class="profile-basic-details__input"
          :readonly="editingField !== 'name'"
          type="text"
          :placeholder="$t('PROFILE_SETTINGS.FORM.NAME.PLACEHOLDER')"
          @input="v$.userName.$touch"
          @keydown.enter.prevent="saveField('name')"
          @keydown.esc="editingField = null"
        />
        <button
          v-if="editingField === 'name'"
          type="button"
          class="profile-basic-details__action profile-basic-details__action--save"
          aria-label="Salvar nome"
          @click="saveField('name')"
        >
          <i class="i-lucide-check" aria-hidden="true" />
        </button>
        <button
          v-else
          type="button"
          class="profile-basic-details__action"
          aria-label="Editar nome"
          @click="activateField('name')"
        >
          <i class="i-lucide-pencil" aria-hidden="true" />
        </button>
      </div>
      <p v-if="v$.userName.$error" class="profile-basic-details__error">
        {{ $t('PROFILE_SETTINGS.FORM.NAME.ERROR') }}
      </p>
    </div>

    <div
      class="profile-basic-details__field"
      :class="{
        'profile-basic-details__field--editing': editingField === 'displayName',
      }"
    >
      <label class="profile-basic-details__label" for="profile-display-name">
        {{ $t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.LABEL') }}
      </label>
      <div class="profile-basic-details__input-row">
        <input
          id="profile-display-name"
          ref="profileDisplayNameInput"
          v-model="userDisplayName"
          class="profile-basic-details__input"
          :readonly="editingField !== 'displayName'"
          type="text"
          :placeholder="$t('PROFILE_SETTINGS.FORM.DISPLAY_NAME.PLACEHOLDER')"
          @keydown.enter.prevent="saveField('displayName')"
          @keydown.esc="editingField = null"
        />
        <button
          v-if="editingField === 'displayName'"
          type="button"
          class="profile-basic-details__action profile-basic-details__action--save"
          aria-label="Salvar nome de exibição"
          @click="saveField('displayName')"
        >
          <i class="i-lucide-check" aria-hidden="true" />
        </button>
        <button
          v-else
          type="button"
          class="profile-basic-details__action"
          aria-label="Editar nome de exibição"
          @click="activateField('displayName')"
        >
          <i class="i-lucide-pencil" aria-hidden="true" />
        </button>
      </div>
    </div>

    <div
      v-if="emailEnabled"
      class="profile-basic-details__field"
      :class="{
        'profile-basic-details__field--editing': editingField === 'email',
        'profile-basic-details__field--error': v$.userEmail.$error,
      }"
    >
      <label class="profile-basic-details__label" for="profile-email">
        {{ $t('PROFILE_SETTINGS.FORM.EMAIL.LABEL') }}
      </label>
      <div class="profile-basic-details__input-row">
        <input
          id="profile-email"
          ref="profileEmailInput"
          v-model="userEmail"
          class="profile-basic-details__input"
          :readonly="editingField !== 'email'"
          type="email"
          :placeholder="$t('PROFILE_SETTINGS.FORM.EMAIL.PLACEHOLDER')"
          @input="v$.userEmail.$touch"
          @keydown.enter.prevent="saveField('email')"
          @keydown.esc="editingField = null"
        />
        <button
          v-if="editingField === 'email'"
          type="button"
          class="profile-basic-details__action profile-basic-details__action--save"
          aria-label="Salvar e-mail"
          @click="saveField('email')"
        >
          <i class="i-lucide-check" aria-hidden="true" />
        </button>
        <button
          v-else
          type="button"
          class="profile-basic-details__action"
          aria-label="Editar e-mail"
          @click="activateField('email')"
        >
          <i class="i-lucide-pencil" aria-hidden="true" />
        </button>
      </div>
      <p v-if="v$.userEmail.$error" class="profile-basic-details__error">
        {{ $t('PROFILE_SETTINGS.FORM.EMAIL.ERROR') }}
      </p>
    </div>
  </section>
</template>

<style scoped>
.profile-basic-details {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.profile-basic-details__field {
  min-width: 0;
}

.profile-basic-details__label {
  display: block;
  margin-bottom: 7px;
  color: #6b7280;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.045em;
  line-height: 1.3;
  text-transform: uppercase;
}

.profile-basic-details__input-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  box-shadow: inset 0 -2px 0 transparent;
  transition: box-shadow 160ms ease;
}

.profile-basic-details__field--editing .profile-basic-details__input-row {
  box-shadow: inset 0 -2px 0 #1b8755;
}

.profile-basic-details__field--error .profile-basic-details__input-row {
  box-shadow: inset 0 -2px 0 #dc2626;
}

.profile-basic-details__input {
  width: 100%;
  min-width: 0;
  height: 42px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #111827;
  font-size: 1rem;
  line-height: 42px;
  outline: none;
}

#profile-name {
  border: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.profile-basic-details__input::placeholder {
  color: #9ca3af;
}

.profile-basic-details__input[readonly] {
  cursor: default;
}

.profile-basic-details__action {
  display: inline-flex;
  width: 38px;
  height: 38px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #6b7280;
  transition: background-color 160ms ease, color 160ms ease;
}

.profile-basic-details__action:hover,
.profile-basic-details__action:focus-visible {
  background: #f3f4f6;
  color: #1b8755;
  outline: none;
}

.profile-basic-details__action--save {
  color: #1b8755;
}

.profile-basic-details__action i {
  width: 19px;
  height: 19px;
  font-size: 19px;
}

.profile-basic-details__error {
  margin: 6px 0 0;
  color: #dc2626;
  font-size: 0.75rem;
  line-height: 1.35;
}

:global(.dark) .profile-basic-details__label {
  color: #a1a1aa;
}

:global(.dark) .profile-basic-details__input {
  color: #f3f4f6;
}

:global(.dark) .profile-basic-details__action:hover,
:global(.dark) .profile-basic-details__action:focus-visible {
  background: #2a2f35;
}
</style>
